import type { HealthCheckFinding, HealthCheckTriageAction } from '../models/healthCheck.types'

import type {
  HealthCheckClock,
  HealthCheckNotifier,
  HealthCheckScopeGetters,
  HealthCheckTriageApi,
  HealthCheckTriageStore,
} from './healthCheckFlow.types'

import { ApiError } from '@/shared/http'

export interface TriageFindingFlowDeps {
  store: HealthCheckTriageStore
  api: HealthCheckTriageApi
  getters: HealthCheckScopeGetters
  clock: HealthCheckClock
  notifier: HealthCheckNotifier
  reload: () => Promise<void>
}

export class TriageFindingFlow {
  constructor(private readonly deps: TriageFindingFlowDeps) {}

  async execute(finding: HealthCheckFinding, action: HealthCheckTriageAction): Promise<void> {
    if (this.deps.store.triagePending[finding.id]) return

    const accountId = this.deps.getters.accountId()
    const workspaceId = this.deps.getters.workspaceId()
    if (accountId == null || workspaceId == null) return

    const previousDismissedAt = finding.dismissedAt
    const optimisticDismissedAt =
      action === 'dismiss' ? new Date(this.deps.clock.now()).toISOString() : null

    this.deps.store.setTriagePending(finding.id, true)
    this.deps.store.applyTriage(finding.id, optimisticDismissedAt)

    try {
      await this.callApi(accountId, workspaceId, finding.id, action)
      this.deps.notifier.success(
        `healthChecks.triage.${action === 'dismiss' ? 'dismissed' : 'restored'}`,
      )
    } catch (cause) {
      if (!this.isCurrentScope(accountId, workspaceId)) return
      this.deps.store.applyTriage(finding.id, previousDismissedAt)
      await this.handleError(cause, action)
    } finally {
      if (this.isCurrentScope(accountId, workspaceId)) {
        this.deps.store.setTriagePending(finding.id, false)
      }
    }
  }

  private callApi(
    accountId: number,
    workspaceId: number,
    findingId: number,
    action: HealthCheckTriageAction,
  ): Promise<void> {
    return action === 'dismiss'
      ? this.deps.api.dismiss(accountId, workspaceId, findingId)
      : this.deps.api.restore(accountId, workspaceId, findingId)
  }

  private async handleError(cause: unknown, action: HealthCheckTriageAction): Promise<void> {
    const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
    if (error.kind === 'canceled') return
    if (error.kind === 'notFound') {
      this.deps.notifier.error('healthChecks.triage.stale')
      await this.deps.reload()
      return
    }
    this.deps.notifier.error(`healthChecks.triage.${action}Failed`)
  }

  private isCurrentScope(accountId: number, workspaceId: number): boolean {
    return (
      this.deps.getters.accountId() === accountId && this.deps.getters.workspaceId() === workspaceId
    )
  }
}
